import {
  Search,
  Trophy,
  Mail,
  CalendarDays,
  HelpCircle,
  UserCircle,
  GraduationCap,
  ArrowRight,
  ListChecks,
} from "lucide-react";
import { CoachIcon } from "@/components/CoachIcon";
import { useBranding } from "@/hooks/useBranding";
import { renderContentfulRichText } from "@/utils/contentfulRichText";
import type { FeatureTip } from "@/hooks/useFeatureTips";

interface PromptCard {
  icon: typeof Search;
  label: string;
  prompt: string;
}

const rankedPrompts: PromptCard[] = [
  {
    icon: Search,
    label: "Find schools that match my profile",
    prompt: "Find schools that match my profile",
  },
  { icon: Trophy, label: "How does my ranking compare?", prompt: "How does my ranking compare?" },
  { icon: Mail, label: "Draft an email to a coach", prompt: "Draft an email to a coach" },
  {
    icon: ListChecks,
    label: "What should I do next?",
    prompt:
      "Look at my profile and My Schools list and tell me the most important thing to do next in my recruiting.",
  },
];

const unrankedPrompts: PromptCard[] = [
  { icon: HelpCircle, label: "How do I get ranked?", prompt: "How do I get ranked?" },
  {
    icon: CalendarDays,
    label: "What tournaments should I play?",
    prompt: "What tournaments should I play?",
  },
  { icon: UserCircle, label: "Build my recruiting profile", prompt: "Build my recruiting profile" },
  {
    icon: GraduationCap,
    label: "What schools might fit me?",
    prompt: "What schools might fit me?",
  },
];

interface WelcomeMessageProps {
  isLinked?: boolean;
  playerName?: string;
  onPromptClick?: (prompt: string) => void;
  discoveryTips?: FeatureTip[];
  onTipAction?: (tip: FeatureTip) => void;
}

function renderIntro(branding: ReturnType<typeof useBranding>["branding"]) {
  const fallback =
    "I'm your personal recruiting coach. Ask about schools that fit your game, how your ranking compares, or how to reach college coaches. I use your profile and real recruiting data from every college golf roster.";

  const rich = branding?.chatIdleRichText;
  if (rich && typeof rich === "object") {
    const rendered = renderContentfulRichText(rich as any, {
      paragraphClassName: "text-center text-[15px] text-muted-foreground leading-relaxed",
    });
    if (rendered) return rendered;
  }

  const intro = fallback;
  return <p className="text-center text-[15px] text-muted-foreground leading-relaxed">{intro}</p>;
}

const WelcomeMessage = ({
  isLinked = false,
  playerName,
  onPromptClick,
  discoveryTips = [],
  onTipAction,
}: WelcomeMessageProps) => {
  const { branding } = useBranding();
  const prompts = isLinked ? rankedPrompts : unrankedPrompts;

  return (
    <div className="flex flex-col items-center justify-center max-w-xl mx-auto px-6 py-8">
      <div className="w-[72px] h-[72px] rounded-full overflow-hidden mb-4 flex items-center justify-center">
        <CoachIcon alt="AI Recruiting Coach" className="h-[72px] w-[72px]" />
      </div>

      <h2 className="mb-3 text-center font-display text-2xl font-extrabold tracking-[-0.03em] text-forest">
        {playerName && playerName !== "Player Name"
          ? `Hey ${playerName}, what can I help with?`
          : "Your recruiting coach"}
      </h2>

      {renderIntro(branding)}

      {onPromptClick && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 w-full max-w-lg">
          {prompts.map(card => {
            const Icon = card.icon;
            return (
              <button
                key={card.label}
                type="button"
                onClick={() => onPromptClick(card.prompt)}
                className="flex items-center gap-3 rounded-xl border border-border/50 bg-card px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/[0.03]"
              >
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-primary-text" />
                </div>
                <span className="text-sm font-medium text-foreground">{card.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {discoveryTips.length > 0 && onTipAction && (
        <div className="mt-6 w-full max-w-lg">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1 bg-border/50" />
            <span className="text-xs text-muted-foreground font-medium">Your next steps</span>
            <div className="h-px flex-1 bg-border/50" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            {discoveryTips.map(tip => {
              const TipIcon = tip.icon;
              return (
                <button
                  key={tip.id}
                  type="button"
                  onClick={() => onTipAction(tip)}
                  className="flex items-center gap-3 rounded-xl border border-primary/20 bg-card px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/[0.03]"
                >
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <TipIcon className="h-4 w-4 text-primary-text" />
                  </div>
                  <span className="text-sm text-foreground flex-1 min-w-0">{tip.description}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomeMessage;
